import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/authStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { supabase } from "@/integrations/supabase/client";
import { CustomerNotificationPrompt } from "@/components/menu/public/CustomerNotificationPrompt";
import { isNotificationSupported } from "@/utils/platformDetection";
import { StoreSelector } from "@/components/menu/StoreSelector";
import { Input } from "@/components/ui/input";
import { PublicMenuHeader } from "@/components/menu/public/PublicMenuHeader";
import { TagsFilterDialog } from "@/components/menu/public/TagsFilterDialog";
import { MenuItemDetailsDialog } from "@/components/menu/public/MenuItemDetailsDialog";
import { PublicMenuCart } from "@/components/menu/public/PublicMenuCart";
import { usePublicMenu } from "@/hooks/usePublicMenu";
import { Table } from "lucide-react";
import { MenuItem } from "@/types/menu";
import { CartItem, Order } from "@/types/orders";
import { PublicMenuTabs } from "@/components/menu/public/page/PublicMenuTabs";
import { MenuContentTab } from "@/components/menu/public/page/MenuContentTab";
import { OrderContentTab } from "@/components/menu/public/page/OrderContentTab";
import { ErrorBoundary } from "react-error-boundary";
import { useOrderStore } from "@/store/orderStore";

import { sendFcmNotification } from "@/services/notification.service";

// Error Fallback Component
function ErrorFallback({ error, resetErrorBoundary }) {
  const { t } = useTranslation();
  const { toast } = useToast();

  useEffect(() => {
    toast({
      title: t("error"),
      description: error.message,
      variant: "destructive",
    });
  }, [error]);

  return (
    <div className="text-center p-4">
      <h2 className="text-lg font-semibold mb-2">{t("somethingWentWrong")}</h2>
      <Button onClick={resetErrorBoundary}>{t("tryAgain")}</Button>
    </div>
  );
}

const MenuSandboxPreview = () => {
  const { t } = useTranslation();
  const { selectedStoreId } = useStoreManagementStore();
  const { user } = useAuthStore();
  const { setSelectedStoreId, initGlobalSubscription, unsubscribeFromOrdersRealtime } = useOrderStore();
  const [tableIdInput, setTableIdInput] = useState("");
  const [simulationActive, setSimulationActive] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(
    null
  );
  const [showItemDetails, setShowItemDetails] = useState(false);
  const [showTagsFilter, setShowTagsFilter] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [activeTab, setActiveTab] = useState("menu");
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>('unsupported');

  useEffect(() => {
    if (isNotificationSupported()) {
      setNotificationPermission(Notification.permission);
    } else {
      setNotificationPermission('unsupported');
    }
  }, []);

  const { toast } = useToast();
  const { stores, fetchStores } = useStoreManagementStore();

  // PublicMenu hook - only activated when simulation starts
  const {
    loading,
    error,
    storeName,
    storeId,
    tableName,
    tableNumber,
    menuItems,
    categories,
    activeOrders,
    availableTags,
    refreshActiveOrders,
  } = usePublicMenu(simulationActive ? tableIdInput : null);

  // Fetch stores on component mount
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Handle table ID submission
  const handleStartSimulation = () => {
    if (!tableIdInput.trim()) {
      toast({
        title: t("error"),
        description: t("pleaseEnterTableId"),
        variant: "destructive",
      });
      return;
    }

    setSimulationActive(true);
    setSelectedCategory("all");
    setSearchTerm("");
    setSelectedTags([]);
  };

  // View item details
  const handleViewDetails = (item: MenuItem) => {
    setSelectedMenuItem(item);
    setShowItemDetails(true);
  };

  // Handle order placed
  const handleOrderPlaced = (order: Order) => {

    
    refreshActiveOrders();
  };

  // Test VAPID configuration
  const handleTestVAPID = async () => {
  
  };

  // Test notification function
  const handleTestNotification = async () => {
    if (!storeId) {
      toast({
        title: t("error"),
        description: "No store selected",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create a test order notification
      console.log('User FCM Token:', user?.fcm_token);
      const testOrderData = {
        token: user?.fcm_token,
        title: 'Test Order Notification',
        body: `Test order #${Date.now()} from Table ${tableNumber || 1}`,
        data: {
          orderId: 'test-order-' + Date.now(),
          storeId: storeId,
          tableId: tableIdInput,
          tableName: tableName || 'Test Table',
          tableNumber: tableNumber || 1,
          status: 'pending',
          totalAmount: 25.99,
          action: 'created',
          items: [
            { name: 'Test Item', quantity: 2 }
          ],
          specialInstructions: 'Test order from MenuSandbox',
          paymentMethod: 'cash',
          tipAmount: 2.99,
          tipPercentage: 10,
          type: 'test_notification'
        }
      };

      // Use the new notification service
      const result = await sendFcmNotification(testOrderData);
      console.log('FCM Notify Success:', result);  
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast({
        title: t("error"),
        description: "Failed to send test notification",
        variant: "destructive",
      });
    }
  };

  // Toggle tag in the filter
  const handleToggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleViewItemDetails = (item: MenuItem) => {
    setSelectedMenuItem(item);
    setShowItemDetails(true);
  };

  const handleAddToCart = (item: MenuItem) => {
    // This function will be passed to PublicMenuCart to handle
    // We'll show a basic toast here and let PublicMenuCart handle the actual cart logic
    toast({
      title: t("addedToCart"),
      description: `${item.name} ${t("hasBeenAddedToYourCart")}`,
      duration: 2000,
    });
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Modified to filter out delivered and completed orders
  const pendingOrders =
    activeOrders?.filter(
      (order) => order.status !== "delivered" && order.status !== "completed"
    ) || [];

  // Updated to check for any non-cancelled, non-delivered, and non-completed orders
  const hasActiveOrders =
    pendingOrders &&
    Array.isArray(pendingOrders) &&
    pendingOrders.filter((order) => order?.status !== "cancelled").length > 0;

  // If not in simulation mode, show the setup screen
  if (!simulationActive) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("menuSandboxPreview.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("menuSandboxPreview.description")}
            </p>
          </div>
          <StoreSelector
            isSelectable={false}
            value={selectedStoreId}
            onValueChange={() => {}}
            className="w-[200px]"
          />
        </div>

        <Card className="border rounded-lg shadow-sm">
          <CardHeader className="bg-primary/10">
            <CardTitle>Public Menu Simulation</CardTitle>
            <CardDescription>
              Enter a table ID to simulate the customer experience when scanning
              a QR code
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-2 items-end">
              <div>
                <label
                  htmlFor="tableId"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Enter Table ID to simulate scanning a QR code
                </label>
                <Input
                  id="tableId"
                  placeholder="Enter Table ID"
                  value={tableIdInput}
                  onChange={(e) => setTableIdInput(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button onClick={handleStartSimulation} className="mb-0">
                <Table className="mr-2 h-4 w-4" />
                Start Simulation
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // In simulation mode, show the public menu experience
  return (
    <div className="container mx-auto py-8 pb-24">
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => {
          // Reset the error boundary state
          setSimulationActive(false);
          setTableIdInput("");
        }}
      >
        <PublicMenuHeader
          storeName={storeName}
          tableName={tableName}
          tableNumber={tableNumber}
        />

        {/* Test Notification Button - Only show in simulation mode */}
        {simulationActive && storeId && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-800 mb-2">
              MenuSandbox Debug Tools
            </h3>
            <div className="flex gap-2">
      
              <Button
                onClick={handleTestNotification}
                variant="outline"
                size="sm"
                className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              >
                Test Notification
              </Button>
              <Button
                onClick={() => {
                  console.log('MenuSandbox - Current state:', {
                    user: user ? { id: user.id, email: user.email, role: user.role } : null,
                    storeId,
                    tableId: tableIdInput,
                    webPushStatus: "Not implemented",
                    notificationPermission: Notification.permission
                  });
                }}
                variant="outline"
                size="sm"
                className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              >
                Log State
              </Button>
            </div>
          </div>
        )}

        {/* Main Tabs */}
        <PublicMenuTabs activeTab={activeTab} setActiveTab={setActiveTab}>
          {/* Menu Tab Content */}
          {activeTab === "menu" ? (
            <MenuContentTab
              menuItems={menuItems}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedTags={selectedTags}
              categories={categories}
              onViewDetails={handleViewItemDetails}
              onAddToCart={handleAddToCart}
              setShowTagsFilter={setShowTagsFilter}
            />
          ) : (
            <OrderContentTab
              pendingOrders={pendingOrders}
              hasActiveOrders={hasActiveOrders}
              activeOrders={activeOrders}
              storeId={storeId || ""}
              tableId={tableIdInput || ""}
              setActiveTab={setActiveTab}
            />
          )}
        </PublicMenuTabs>

        {/* Menu Item Details Dialog */}
        <MenuItemDetailsDialog
          open={showItemDetails}
          onOpenChange={setShowItemDetails}
          item={selectedMenuItem}
          onAddToCart={handleAddToCart}
        />

        {/* Tags Filter Dialog */}
        <TagsFilterDialog
          isOpen={showTagsFilter}
          onOpenChange={setShowTagsFilter}
          availableTags={availableTags}
          selectedTags={selectedTags}
          onToggleTag={toggleTag}
          onClearAll={() => setSelectedTags([])}
        />

        {/* Shopping Cart - Only render when all required props are valid */}
        {tableIdInput &&
          storeId &&
          tableName &&
          typeof tableNumber === "number" &&
          !isNaN(tableNumber) && (
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onReset={() => {
                // Reset the cart-specific state if needed
                setCartItems([]);
              }}
            >
              <PublicMenuCart
                tableId={tableIdInput}
                tableName={tableName}
                tableNumber={tableNumber}
                storeId={storeId}
                onOrderPlaced={handleOrderPlaced}
              />
            </ErrorBoundary>
          )}
      </ErrorBoundary>
    </div>
  );
};

export default MenuSandboxPreview;
